import { computed } from "vue";
import { useStore } from "vuex";
import {
  formatDateWithTimezone,
  formatTimeWithTimezone,
  isFalsy
} from "@/lib/helpers";
import provideHook from "@/lib/provideHook";

import ClockIcon from "@/images/Alarm.svg?component";
import CheckIcon from "@/images/check.svg?component";
import CheckScheduler from "@/images/CheckScheduler.svg?component";
import ClaimedIcon from "@/images/ClaimedIcon.svg?component";
import ExclamationIcon from "@/images/exclamation.svg?component";
import { formatDuration } from "@/mixins";
import { useScheduleColors } from "@/SchedulerV2/useScheduleColors";
import type { AppointmentV2 } from "@/typings/appointment";
import { AppointmentAdapter, TimelineStep } from "@/typings/scheduler";

export const useAppointmentTimes = provideHook(
  (props: {
    appointment: AppointmentV2;
    editableTimes?: string[];
    lostTimes?: string[];
  }) => {
    const store = useStore();
    const preWorkEnabled = computed(() =>
      store.getters.settingEnabledForTenant("pre_work_enabled")
    );
    const { getStatusCustomizationName } = useScheduleColors();
    const statusName = computed(() => {
      const adapter = new AppointmentAdapter();
      const _appointment = adapter.toSchedulerAppointment(props.appointment);
      return getStatusCustomizationName(_appointment);
    });

    // ---- Centralized color/icon logic ----
    type StepType =
      | "pending"
      | "claimed"
      | "traveling"
      | "arrival"
      | "departure"
      | "notActive";

    interface StepColorConfig {
      circleFill: string;
      circleBorder: string;
      lineColor: string;
      iconColor: string;
      customIcon: string;
      grey?: boolean;
    }

    function getStepColors(
      type: StepType,
      options: {
        active?: boolean;
        completed?: boolean;
        editable?: boolean;
        lost?: boolean;
      } = {}
    ): StepColorConfig {
      const { active, completed, editable, lost } = options;

      if (lost) {
        return {
          circleFill: "#FFF",
          circleBorder: "#F05252",
          lineColor: "#F05252",
          iconColor: "#FFF",
          customIcon: ExclamationIcon,
          grey: false
        };
      }

      if (editable) {
        return {
          circleFill: "#FFF",
          circleBorder: "#F05252",
          lineColor: "#F05252",
          iconColor: "#FFF",
          customIcon: ExclamationIcon,
          grey: false
        };
      }

      if (type == "pending") {
        return {
          circleFill: "#F3F4F6",
          circleBorder: "#1C64F2",
          lineColor: "#1C64F2",
          iconColor: "transparent",
          customIcon: "",
          grey: false
        };
      }

      if (active) {
        return {
          circleFill: "",
          customIcon: ClaimedIcon,
          lineColor: "#1C64F2",
          iconColor: "",
          circleBorder: "#1C64F2",
          grey: false
        };
      }

      if (type == "notActive") {
        // Not active steps should be grey
        return {
          circleFill: "#fff",
          circleBorder: "#E5E7EB",
          lineColor: "#E5E7EB",
          iconColor: "#fff",
          customIcon: "",
          grey: true
        };
      }

      if (completed) {
        return {
          circleFill: "#0E9F6E",
          circleBorder: "#0E9F6E",
          lineColor: "#0E9F6E",
          iconColor: "#fff",
          customIcon: CheckScheduler,
          grey: false
        };
      }

      // Default fallback
      return {
        circleFill: "#E5E7EB",
        circleBorder: "#E5E7EB",
        lineColor: "#E5E7EB",
        iconColor: "#6B7280",
        customIcon: "",
        grey: false
      };
    }

    function getStepClass(step, statusName) {
      const status =
        statusName && statusName.value !== undefined
          ? statusName.value
          : statusName;

      if (step.lost) {
        return "lost-step";
      }
      if (step.editable && !step.optional) {
        return "editable-step";
      }

      if (step.active && status === "pending_assigned") {
        return "pending-assigned-step";
      }
      if (step.active) {
        return "active-step";
      }
      if (step.title === "Completed" && !step.active && step.completed) {
        return "pending-assigned-step completed";
      }
      return "";
    }
    // ---- End centralized color/icon logic ----

    const timeBadge = computed(() => {
      const values = {
        label: "",
        time: "",
        textOnly: false,
        icon: ClockIcon,
        backgroundColor: "bg-teal-100"
      };
      switch (statusName.value) {
        case "pending_unassigned":
        case "pending_assigned": {
          values.label = "Pending Acceptance";
          values.time = "--";
          values.textOnly = true;
          values.backgroundColor = "bg-gray-200";
          break;
        }
        case "accepted_pre_work":
        case "accepted": {
          values.label = "Pre-Work";
          values.time = claimedStep().duration;
          break;
        }
        case "late":
        case "traveling": {
          values.label = "Accepted";
          values.time = travelStep().duration;
          break;
        }
        case "running": {
          values.label = "Working";
          values.time = arrivalStep().duration;
          break;
        }
        case "compeleted_won_opportunity":
        case "compeleted_lost_opportunity": {
          values.label = "Completed";
          values.time = departureStep().duration;
          values.backgroundColor = "bg-green-100";
          values.icon = CheckIcon;
          break;
        }
      }
      return values;
    });

    const steps = computed(() => {
      let steps = [] as TimelineStep[];

      switch (statusName.value.toString()) {
        case "pending_assigned": {
          steps = [
            props.editableTimes?.includes("accepted_time") &&
            props.lostTimes?.length === 0
              ? claimedStep(false)
              : pendingStep(true),
            new TimelineStep({
              title: "Travel",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("dispatch_time") &&
                    props.lostTimes?.length === 0
                )
              }),
              editable: Boolean(
                props.editableTimes?.includes("dispatch_time") &&
                  props.lostTimes?.length === 0
              ),
              editableFieldName: "dispatch_time",
              grey: true
            }),
            new TimelineStep({
              title: "Arrival",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(props.editableTimes?.includes("arrival_time"))
              }),
              editable: Boolean(props.editableTimes?.includes("arrival_time")),
              editableFieldName: "arrival_time",
              grey: true
            }),
            new TimelineStep({
              title: "Departure",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("departure_time")
                )
              }),
              editable: Boolean(
                props.editableTimes?.includes("departure_time")
              ),
              editableFieldName: "departure_time",
              grey: true
            })
          ];
          break;
        }
        case "accepted_pre_work": {
          steps = [
            claimedStep(true),
            new TimelineStep({
              title: "Travel",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("dispatch_time")
                )
              }),
              editable: Boolean(props.editableTimes?.includes("dispatch_time")),
              editableFieldName: "dispatch_time",
              grey: true
            }),
            new TimelineStep({
              title: "Arrival",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(props.editableTimes?.includes("arrival_time"))
              }),
              editable: Boolean(props.editableTimes?.includes("arrival_time")),
              editableFieldName: "arrival_time",
              grey: true
            }),
            new TimelineStep({
              title: "Departure",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("departure_time")
                )
              }),
              editable: Boolean(
                props.editableTimes?.includes("departure_time")
              ),
              editableFieldName: "departure_time",
              grey: true
            })
          ];
          break;
        }
        case "late":
        case "traveling": {
          steps = [
            claimedStep(false, true),
            travelStep(true),
            new TimelineStep({
              title: "Arrival",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(props.editableTimes?.includes("arrival_time"))
              }),
              editable: Boolean(props.editableTimes?.includes("arrival_time")),
              editableFieldName: "arrival_time",
              grey: true
            }),
            new TimelineStep({
              title: "Departure",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("departure_time")
                )
              }),
              editable: Boolean(
                props.editableTimes?.includes("departure_time")
              ),
              editableFieldName: "departure_time",
              grey: true
            })
          ];
          break;
        }
        case "running": {
          steps = [
            claimedStep(false, true),
            travelStep(false, true),
            arrivalStep(true, false),
            new TimelineStep({
              title: "Departure",
              ...getStepColors("notActive", {
                active: false,
                editable: Boolean(
                  props.editableTimes?.includes("departure_time")
                )
              }),
              editable: Boolean(
                props.editableTimes?.includes("departure_time")
              ),
              editableFieldName: "departure_time",
              grey: true
            })
          ];
          break;
        }
        case "compeleted_won_opportunity":
        case "compeleted_lost_opportunity": {
          steps = [
            claimedStep(false, true),
            travelStep(false, true),
            arrivalStep(false, true),
            departureStep(false, true)
          ];
          break;
        }
      }

      if (!preWorkEnabled.value) {
        return steps.filter((s) => s.title !== "Claimed");
      }
      return steps;
    });

    const pendingStep = (active = false) => {
      const { appointment } = props;
      return {
        title: "Arrival Window",
        time: `${formatTimeWithTimezone(
          appointment.arrival_window_start
        )}-${formatTimeWithTimezone(appointment.arrival_window_end)}`,
        date: formatDateWithTimezone(appointment.arrival_window_start),
        duration: formatDuration(appointment.estimated_duration_minutes),
        durationLabel: "Est. Job Duration",
        active,
        editable: false,
        ...getStepColors("pending", { active })
      };
    };

    const claimedStep = (active = false, completed = false) => {
      const { appointment } = props;
      const isEditable = Boolean(
        props.editableTimes?.includes("accepted_time") &&
          props.lostTimes?.length === 0 &&
          !appointment.accepted_time
      );
      return {
        title: "Claimed",
        time: appointment.accepted_time
          ? formatTimeWithTimezone(appointment.accepted_time)
          : "",
        date: appointment.accepted_time
          ? formatDateWithTimezone(appointment.accepted_time)
          : "",
        duration: isFalsy(appointment.pre_work_minutes)
          ? "--"
          : formatDuration(appointment.pre_work_minutes),
        durationLabel: "Pre-work",
        active,
        editable: isEditable,
        optional: true,
        editableFieldName: "accepted_time",
        lost: Boolean(props.lostTimes?.includes("accepted_time")),
        completed,
        ...getStepColors("claimed", {
          active,
          completed,
          lost: Boolean(props.lostTimes?.includes("accepted_time"))
        })
      };
    };

    const travelStep = (active = false, completed = false) => {
      const { appointment } = props;
      return {
        title: "Travel Started",
        time: formatTimeWithTimezone(appointment.dispatch_time),
        date: formatDateWithTimezone(appointment.dispatch_time),
        duration: isFalsy(appointment.travel_minutes)
          ? "--"
          : formatDuration(appointment.travel_minutes),
        durationLabel: "Drive Time",
        active,
        editable: Boolean(
          props.editableTimes?.includes("dispatch_time") &&
            props.lostTimes?.length === 0 &&
            !appointment.dispatch_time
        ),
        lost: Boolean(props.lostTimes?.includes("dispatch_time")),
        completed,
        ...getStepColors("traveling", {
          active,
          completed,
          lost: Boolean(props.lostTimes?.includes("dispatch_time"))
        })
      };
    };

    const arrivalStep = (active = false, completed = false) => {
      const { appointment } = props;
      return {
        title: "Arrived",
        time: formatTimeWithTimezone(appointment.arrival_time),
        date: formatDateWithTimezone(appointment.arrival_time),
        duration: isFalsy(appointment.work_minutes)
          ? "--"
          : formatDuration(appointment.work_minutes),
        durationLabel: "Work Duration",
        active,
        editable: Boolean(
          props.editableTimes?.includes("arrival_time") &&
            props.lostTimes?.length === 0 &&
            !appointment.arrival_time
        ),
        lost: Boolean(props.lostTimes?.includes("arrival_time")),
        completed,
        ...getStepColors("arrival", {
          active,
          completed,
          lost: Boolean(props.lostTimes?.includes("arrival_time"))
        })
      };
    };

    const departureStep = (active = false, completed = false) => {
      const { appointment } = props;
      return {
        title: "Completed",
        time: formatTimeWithTimezone(appointment.departure_time),
        date: formatDateWithTimezone(appointment.departure_time),
        duration: isFalsy(appointment.duration_minutes)
          ? "--"
          : formatDuration(appointment.duration_minutes),
        durationLabel: "Total Duration",
        active,
        editable: Boolean(
          props.editableTimes?.includes("departure_time") &&
            props.lostTimes?.length === 0 &&
            !appointment.departure_time
        ),
        lost: Boolean(props.lostTimes?.includes("departure_time")),
        completed,
        ...getStepColors("departure", {
          active,
          completed,
          lost: Boolean(props.lostTimes?.includes("departure_time"))
        })
      };
    };
    return {
      timeBadge,
      steps,
      statusName,
      getStepClass,
      claimedStep,
      travelStep,
      arrivalStep,
      departureStep
    };
  }
);
